import type { GroupProgressResponse } from '@echo/api-client/echo';
import { EXERCISE_ORDER, type ExerciseKind } from '../domain/exercise';
import type { GroupDashboardAvatar, GroupDashboardDetails } from '../domain/group-dashboard';
import { addDays } from './calendar';
import type { DoneBy, MemberDay } from './mark-rules';
import { type DayLog, logRows, WEEK_DAYS, type WeekBar } from './progress';
import { SM_KIND } from './schemas';

/**
 * A sub-group's progress as echo's backend answers it (`GET /v1/groups/{id}/progress`,
 * specs/v1-2-0/01-group-progress-and-marks.md, section 2): completion only, never answers,
 * timings or exercise photos. `days` come to everyone in the group, `marks` to its leaders only.
 */
export type {
  GroupMemberProgress,
  GroupProgressResponse as SubGroupProgress,
} from '@echo/api-client/echo';

/** What the group sees of one member's week, in the shapes the progress cards draw. */
export interface MemberWeekView {
  /** `until` and the days before it in this week, newest first. */
  log: DayLog[];
  /** The first day the backend answers for, where the log's date selector stops. */
  firstDate: string;
  /** Monday to Sunday; days still to come read 0. */
  week: WeekBar[];
  exercisesThisWeek: number;
  /** Share of the week's seven days on which each exercise was done, 0..1. */
  completionByKind: Record<ExerciseKind, number>;
  /** Today's exercises and who finished each: the member, a leader, or nobody yet. */
  today: Record<ExerciseKind, DoneBy>;
  /**
   * Today's exercises a leader of this group marked, the only marks this group can undo. A
   * leader of another group the member is in may have marked one too: it counts as done here,
   * but only that group can take it back.
   */
  undoable: Record<ExerciseKind, boolean>;
}

/**
 * A member's week from the backend's days, counted like the person's own Progress
 * (`deriveProgress`): marks count as done. Undefined without days, which an older backend gave
 * to leaders only.
 */
export function memberWeekView(
  member: {
    days?: readonly MemberDay[];
    /** This group's marks that still say done; only leaders get them. */
    marks?: readonly { date: string; kind: string }[];
  },
  today: string,
  until = today,
): MemberWeekView | undefined {
  const days = member.days;
  if (!days) return undefined;
  const byDate = new Map(days.map((day) => [day.date, day]));
  const doneBy = (date: string): Record<ExerciseKind, DoneBy> => {
    const day = byDate.get(date);
    return {
      reading: day?.[SM_KIND.reading] ?? null,
      writing: day?.[SM_KIND.writing] ?? null,
      math: day?.[SM_KIND.math] ?? null,
    };
  };

  const ours = (kind: string) =>
    (member.marks ?? []).some((mark) => mark.date === today && mark.kind === kind);

  // The backend answers for this week only: earlier days are unknown, not undone.
  const firstDate = days.reduce((first, day) => (day.date < first ? day.date : first), today);
  const log = logRows(
    (date) => {
      const by = doneBy(date);
      return { reading: !!by.reading, writing: !!by.writing, math: !!by.math };
    },
    today,
    until,
    firstDate,
  );

  const daysWithKind = { reading: 0, writing: 0, math: 0 };
  const week: WeekBar[] = days.slice(0, WEEK_DAYS).map((day) => {
    const by = doneBy(day.date);
    const kinds = EXERCISE_ORDER.filter((kind) => by[kind]);
    for (const kind of kinds) daysWithKind[kind] += 1;
    return { date: day.date, exercisesDone: kinds.length };
  });

  return {
    log,
    firstDate,
    week,
    exercisesThisWeek: week.reduce((sum, bar) => sum + bar.exercisesDone, 0),
    completionByKind: {
      reading: daysWithKind.reading / WEEK_DAYS,
      writing: daysWithKind.writing / WEEK_DAYS,
      math: daysWithKind.math / WEEK_DAYS,
    },
    today: doneBy(today),
    undoable: {
      reading: ours(SM_KIND.reading),
      writing: ours(SM_KIND.writing),
      math: ours(SM_KIND.math),
    },
  };
}

/**
 * The day's mood on a Groups card (Figma card/group): everyone done, at least half of them,
 * or fewer — each with its own line, emoji and bar colour.
 */
export type GroupDayMood = 'finished' | 'great' | 'early';

/** One card of the Groups tab: who leads the group and how its day is going. */
export interface GroupDayCard {
  /** "Led by …": a Facilitator before a Group Leader, someone else before you. */
  leader: { name: string; you: boolean } | undefined;
  /** Members who do the exercises — Facilitators don't — and how many finished all three today. */
  completed: number;
  total: number;
  mood: GroupDayMood;
}

/** "Maria T.": a first name and the initial of the last, or whichever of them there is. */
export function shortName(person: { name: string; firstName: string; lastName: string }): string {
  const first = person.firstName.trim();
  const last = person.lastName.trim();
  if (first && last) return `${first} ${last.charAt(0)}.`;
  return first || last || person.name;
}

export function groupDayCard(progress: GroupProgressResponse): GroupDayCard {
  const leaders = progress.members
    .filter((member) => member.leader)
    .sort((a, b) => Number(b.facilitator) - Number(a.facilitator) || Number(a.you) - Number(b.you));
  const [lead] = leaders;
  const doing = progress.members.filter((member) => !member.facilitator);
  const completed = doing.filter((member) => member.daysDone.includes(progress.today)).length;
  const total = doing.length;
  const mood: GroupDayMood =
    total > 0 && completed === total
      ? 'finished'
      : completed * 2 >= total && completed > 0
        ? 'great'
        : 'early';
  return {
    leader: lead ? { name: shortName(lead), you: lead.you } : undefined,
    completed,
    total,
    mood,
  };
}

/** Faces of the members done today on the group overview; the rest are counted, "+3". */
const OVERVIEW_FACES = 4;
const DAY_KINDS = ['read', 'write', 'math'] as const;

type GroupMember = GroupProgressResponse['members'][number];

function photoOf(member: GroupMember): GroupDashboardAvatar | undefined {
  return member.photoUrl ? { kind: 'remote', url: member.photoUrl } : undefined;
}

/**
 * The group profile's statistics, from the same week the Groups card reads. Only members who
 * do the exercises count (Facilitators don't), and only days up to today. Everyone is listed
 * in `members`, Facilitators by their role.
 * - Team streak: days in a row everyone has done — the shortest current streak among them, a
 *   streak counting only from a summary of today or yesterday.
 * - This month: the sessions they completed this month, by their newest summaries.
 * - Each day of the week: how many of the three exercises a member did, on average.
 * - Each exercise: how often it was done, of the days this week so far.
 */
export function groupDashboardDetails(progress: GroupProgressResponse): GroupDashboardDetails {
  const card = groupDayCard(progress);
  const doing = progress.members.filter((member) => !member.facilitator);
  const doneToday = doing.filter((member) => member.daysDone.includes(progress.today));
  const you = progress.members.find((member) => member.you);
  const week = Array.from({ length: WEEK_DAYS }, (_, offset) =>
    addDays(progress.weekStart, offset),
  );
  const sofar = week.filter((date) => date <= progress.today);
  const dayOf = (member: GroupMember, date: string) =>
    member.days?.find((day) => day.date === date);
  const kindsDone = (member: GroupMember, date: string) => {
    const day = dayOf(member, date);
    return day ? DAY_KINDS.filter((kind) => day[kind] !== null).length : 0;
  };
  const rate = (kind: (typeof DAY_KINDS)[number]) => {
    const slots = doing.length * sofar.length;
    const done = doing.reduce(
      (sum, member) => sum + sofar.filter((date) => dayOf(member, date)?.[kind] != null).length,
      0,
    );
    return slots > 0 ? done / slots : 0;
  };
  const month = progress.today.slice(0, 7);
  return {
    leaderName: card.leader?.name,
    leaderIsYou: card.leader?.you,
    avatarTone: 'olive',
    memberCount: progress.members.length,
    role: you ? (you.facilitator ? 'facilitator' : you.leader ? 'leader' : 'member') : undefined,
    today: {
      done: card.completed,
      total: card.total,
      energy: card.mood === 'finished' ? 'complete' : card.mood,
    },
    overview: {
      completed: card.completed,
      total: card.total,
      streak:
        doing.length > 0
          ? Math.min(
              ...doing.map((member) =>
                // A streak is only true on its summary's day: one from before yesterday is over.
                member.summary && member.summary.asOf >= addDays(progress.today, -1)
                  ? member.summary.currentStreak
                  : 0,
              ),
            )
          : 0,
      month: doing.reduce(
        (sum, member) =>
          sum + (member.summary?.asOf.startsWith(month) ? member.summary.sessionsThisMonth : 0),
        0,
      ),
      faces: doneToday
        .slice(0, OVERVIEW_FACES)
        .map((member) => ({ avatar: photoOf(member), label: shortName(member) })),
      overflow: Math.max(0, doneToday.length - OVERVIEW_FACES),
    },
    weekStart: progress.weekStart,
    weekAverages: week.map((date) =>
      date > progress.today || doing.length === 0
        ? 0
        : doing.reduce((sum, member) => sum + kindsDone(member, date), 0) / doing.length,
    ),
    exerciseCompletion: { reading: rate('read'), writing: rate('write'), math: rate('math') },
    members: progress.members.map((member) => ({
      id: member.actorUuid,
      name: shortName(member),
      role: member.facilitator ? 'facilitator' : member.leader ? 'leader' : undefined,
      avatar: photoOf(member),
      done: member.done,
      total: member.target,
    })),
  };
}
